import Link from 'next/link';
import { useRouter } from 'next/router';

import React from 'react';

import styled from 'styled-components';

import { serviceBookingCode, serviceName } from '@utils/find-name';
import { getSlugFromServiceCode } from '@utils/get-service-code';

import { Partner } from '@customTypes/Partner';
import { Services } from '@customTypes/Service';

interface PartnerBreadcrumbProps {
  services: Services;
  partner: Partner;
}

interface DirectoryBreadcrumbProps {
  serviceId: number;
  services: Services;
}

interface BlogBreadcrumbProps {
  category: string;
  title: string;
}

const Wrapper = styled.div<{ hasPadding?: boolean }>`
  display: flex;
  flex-wrap: wrap;
  font-weight: 500;
  font-size: 15px;
  padding: ${({ hasPadding }) => (hasPadding ? '0 24px' : '0')};
  color: #a3a7ae;
  margin-bottom: 30px;
  @media (min-width: 992px) {
    margin-top: 30px;
    margin-bottom: 40px;
  }

  a {
    display: flex;
    align-items: center;
    margin-bottom: 4px;
    &:not(:last-child)::after {
      content: '';
      display: inline-block;
      background: url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjIiIGhlaWdodD0iMjIiIHZpZXdCb3g9IjAgMCAyMiAyMiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTguMjUgMTYuMjM5NkwxMi44MzMzIDExLjI0NDJMOC4yNSA2LjE1NjI1IiBzdHJva2U9IiNBM0E3QUUiIHN0cm9rZS13aWR0aD0iMS41Ii8+Cjwvc3ZnPgo=');
      width: 22px;
      height: 22px;
    }
    &:last-child {
      color: #080808;
      user-select: none;
      pointer-events: none;
      display: -webkit-box;
      -webkit-line-clamp: 1;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
`;

export const PartnerBreadcrumb: React.FC<PartnerBreadcrumbProps> = (props) => {
  const { services, partner } = props;
  const { query } = useRouter();
  const $serviceId = query.serviceId ? parseInt(`${query.serviceId}`) : null;
  const defaultServiceId = partner.primary_service_id;
  const serviceId = $serviceId || defaultServiceId;
  const serviceLabel = serviceId ? serviceName(serviceId, services) : '';
  const levels: { href: string; label: string }[] = serviceId
    ? [
        {
          href: `/${getSlugFromServiceCode(serviceBookingCode(serviceId, services))}`,
          label: serviceLabel,
        },
        {
          href: `/${getSlugFromServiceCode(serviceBookingCode(serviceId, services))}/partners`,
          label: `${serviceLabel} 파트너`,
        },
        {
          href: `/partners/${partner.id}`,
          label: partner.name,
        },
      ]
    : [
        {
          href: `/partners`,
          label: `파트너`,
        },
        {
          href: `/partners/${partner.id}`,
          label: partner.name,
        },
      ];

  return (
    <Wrapper>
      {levels.map(({ href, label }) => (
        <Link key={href} href={href}>
          {label}
        </Link>
      ))}
    </Wrapper>
  );
};

export const DirectoryBreadcrumb: React.FC<DirectoryBreadcrumbProps> = (props) => {
  const { services, serviceId } = props;
  const serviceLabel = serviceName(serviceId, services);
  const levels: { href: string; label: string }[] = [
    {
      href: `/${getSlugFromServiceCode(serviceBookingCode(serviceId, services))}`,
      label: serviceLabel,
    },
    {
      href: `/${getSlugFromServiceCode(serviceBookingCode(serviceId, services))}/partners`,
      label: `${serviceLabel} 파트너`,
    },
  ];

  return (
    <Wrapper>
      {levels.map(({ href, label }) => (
        <Link key={href} href={href}>
          {label}
        </Link>
      ))}
    </Wrapper>
  );
};

export const BlogBreadcrumb = ({ category, title }: BlogBreadcrumbProps): JSX.Element => {
  const levels: { href: string; label: string }[] = [
    {
      href: `/blog`,
      label: `블로그`,
    },
    {
      href: `/blog?category=${category}`,
      label: `${category}`,
    },
    {
      href: `/blog/${category}/${title}`,
      label: `${title}`,
    },
  ];
  return (
    <Wrapper>
      {levels.map(({ href, label }) => (
        <Link key={href} href={href}>
          {label}
        </Link>
      ))}
    </Wrapper>
  );
};
